import clsx from "clsx";
import React from "react";
import { Button } from "web/react/emo/button";
import { Heading } from "web/react/emo/heading";
import { Text } from "web/react/emo/text";
import { VStack } from "web/react/emo/v-stack/v-stack";
import { gettext } from "web/script/modules/django-i18n";
import * as styles from "./story-details.css";

export type StoryDetailObject = {
    isSponsored: boolean;
    title: string;
    caption: string | null;
    url: string;
};

interface StoryDetailsProps {
    variant?: "primary" | "secondary" | "compact";
    details: StoryDetailObject;
    isBodyClickable?: boolean;
    onCtaClick: () => void;
    customButtonText?: string;
    captionLineLimit?: number;
    titleLineLimit?: number;
}

/**
 * Design System
 * @name StoryDetails
 * @version 1.0
 * @design https://www.figma.com/file/EJXteyeEMjMgKETDQiZDli/Design-Sprint%3A-FY24-Home?type=design&node-id=3767-78162&mode=design&t=Otf1m3aYXI7YYf4b-4
 */
export function StoryDetails({
    variant = "primary",
    details,
    isBodyClickable,
    onCtaClick,
    customButtonText,
    captionLineLimit,
    titleLineLimit,
}: StoryDetailsProps): React.ReactElement {
    const { isSponsored, title, caption, url } = details;
    const isPrimary = variant === "primary";
    const isSecondary = variant === "secondary";
    const isCompact = variant === "compact";

    const bodyText = (
        <Text
            as="p"
            textStyle={isCompact ? "body-s" : "body"}
            className={styles.body}
            lineLimit={captionLineLimit}
            id="story-details-description"
        >
            {caption}
        </Text>
    );

    return (
        <section className={clsx({ [styles.wrapper]: !isCompact })} data-testid={"story-details"}>
            <div
                className={clsx(styles.content, {
                    [styles.secondaryDesktopContent]: isSecondary,
                    [styles.primaryContent]: isPrimary,
                })}
            >
                <VStack spacing={isPrimary ? "xm" : "xxs"}>
                    <VStack spacing={"xs"}>
                        {isSponsored && (
                            <Text textStyle={"caption-3"} className={styles.sponsored}>
                                {gettext("general.sponsored_label")}
                            </Text>
                        )}

                        <Heading
                            upperCase={isCompact}
                            textStyle={
                                isCompact
                                    ? "title-s"
                                    : {
                                          lg: "hero-l",
                                          md: "hero-l",
                                          sm: "large-title-2",
                                      }
                            }
                            wordBreak
                            lineLimit={titleLineLimit}
                        >
                            {title}
                        </Heading>
                    </VStack>
                    {bodyText && isBodyClickable ? (
                        <a href={url} data-testid={"body-text"}>
                            {bodyText}
                        </a>
                    ) : (
                        bodyText
                    )}
                </VStack>
                <div
                    className={clsx({ [styles.buttonWrapper]: isPrimary })}
                    data-testid="cta-button"
                >
                    <Button
                        href={url}
                        title={customButtonText || gettext("general.read_more")}
                        width={isPrimary ? "full" : "fit-content"}
                        variant={isPrimary ? "primary" : "secondary"}
                        onClick={onCtaClick}
                        ariaDescribedBy="story-details-description"
                        dataTestId="story-details-cta"
                    />
                </div>
            </div>
        </section>
    );
}
